import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type * as React from 'react'

import { cn } from '~/shared/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md squircle text-sm font-semibold tracking-[-0.01em] transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-spring cursor-pointer select-none disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:m-0 [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 active:not-disabled:scale-[0.97]",
  {
    variants: {
      variant: {
        default:
          'bg-foreground text-background shadow-[inset_0_1px_0_rgb(255_255_255/0.12)] hover:bg-foreground/85',
        brand:
          'bg-brand text-primary-foreground shadow-[inset_0_1px_0_rgb(255_255_255/0.3)] hover:bg-brand-strong',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline:
          'border border-foreground/15 bg-transparent text-foreground hover:bg-foreground/[0.06] hover:border-foreground/25',
        secondary: 'bg-foreground/[0.07] text-foreground hover:bg-foreground/[0.12]',
        ghost: 'text-foreground/80 hover:bg-foreground/[0.06] hover:text-foreground',
        link: 'rounded-md text-brand-subtle-foreground underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 has-[>svg]:pl-3.5',
        sm: 'h-8 gap-1 px-3 text-[13px] has-[>svg]:pl-2.5',
        lg: 'h-10 px-5 text-[15px] has-[>svg]:pl-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
